import { Button } from '@carbon/react';
import { Add, TrashCan } from '@carbon/react/icons';
import { uniqueId } from 'lodash-es';
import {
  BufferedNumberInput,
  parseInteger,
  parseNumber,
} from './buffered-number-input';
import type { StepSegment } from './types';
import styles from './segment-list.module.scss';

interface SegmentListProps {
  segments: StepSegment[];
  /** Display unit shown next to each segment's value input (e.g. "$/bbl", "%"). */
  unit: string;
  /**
   * Value used when adding a new segment (or seeding `value` on a fresh blank).
   * For top-level Stepped profiles this is unused; for Custom-curve tail it's
   * the last curve point's value so the editor pre-fills sensibly.
   */
  anchorValue?: number;
  onChange: (next: StepSegment[]) => void;
  /** DOM id namespace so multiple instances on the same page don't collide. */
  idPrefix: string;
}

/**
 * Builds a fresh open-ended segment, optionally pre-filling the value with an
 * anchor (e.g. the last imported curve value when seeding a tail's segments).
 * @param {number | undefined} anchorValue - Optional value to pre-fill
 * @return {StepSegment} A new segment
 */
function makeBlankSegment(anchorValue: number | undefined): StepSegment {
  return {
    id: Number(uniqueId()),
    durationMonths: 12,
    value: anchorValue !== undefined ? anchorValue : '',
  };
}

/**
 * Prop-driven stepped-schedule editor. Always renders ≥ 1 segment; the terminal
 * segment is always open-ended (`durationMonths: null`). Add converts the
 * current terminal to a finite duration and appends a new open-ended one;
 * Remove maintains the open-ended invariant on the new terminal.
 * @param {SegmentListProps} props - The editor props
 * @return {JSX.Element} The segment list editor
 */
export function SegmentList({
  segments,
  unit,
  anchorValue,
  onChange,
  idPrefix,
}: SegmentListProps): JSX.Element {
  const updateSegment = (index: number, patch: Partial<StepSegment>): void => {
    const next = segments.map((s, i) => (i === index ? { ...s, ...patch } : s));
    onChange(next);
  };

  const addSegment = (): void => {
    // The current terminal's duration input remounts via the isLast key suffix
    // changing (rem → fin), so no id change is needed. Its months value is left
    // as-is (blank if untouched, user's value if typed) — same behaviour as the
    // value/bbl field — so nothing is pre-filled without the user's input.
    const next = [
      ...segments,
      { ...makeBlankSegment(anchorValue), durationMonths: null },
    ];
    onChange(next);
  };

  const removeSegment = (index: number): void => {
    if (segments.length <= 1) return;
    const next = segments.filter((_, i) => i !== index);
    onChange(next);
  };

  return (
    <div>
      <div className={styles.segments}>
        {segments.map((seg, index) => {
          const isLast = index === segments.length - 1;
          return (
            <div key={seg.id} className={styles.segmentRow}>
              <span className={styles.segmentLabel}>{`Seg ${index + 1}`}</span>
              <BufferedNumberInput
                // Key includes an isLast suffix ('r' = remainder, 'f' = fixed)
                // so the duration input remounts when terminal status changes
                // without affecting the value input's key (which uses seg.id
                // alone and stays stable across terminal promotions).
                key={`${idPrefix}-${seg.id}-dur-${isLast ? 'r' : 'f'}`}
                id={`${idPrefix}-${seg.id}-dur`}
                labelText="Duration (months)"
                hideLabel
                step="1"
                disabled={false}
                initial={
                  seg.durationMonths === null || seg.durationMonths === ''
                    ? ''
                    : String(seg.durationMonths)
                }
                placeholder={isLast ? 'Remainder' : 'months'}
                onCommit={(raw) =>
                  updateSegment(index, { durationMonths: parseInteger(raw) })
                }
              />
              <span className={styles.segmentUnit}>months</span>
              <BufferedNumberInput
                key={`${idPrefix}-${seg.id}-val`}
                id={`${idPrefix}-${seg.id}-val`}
                labelText="Segment value"
                hideLabel
                initial={seg.value === '' ? '' : String(seg.value)}
                placeholder="—"
                onCommit={(raw) =>
                  updateSegment(index, { value: parseNumber(raw) })
                }
              />
              <span className={styles.segmentUnit}>{unit}</span>
              <Button
                kind="danger--ghost"
                size="sm"
                hasIconOnly
                renderIcon={TrashCan}
                iconDescription="Remove segment"
                tooltipPosition="top"
                disabled={segments.length <= 1}
                onClick={() => removeSegment(index)}
              />
            </div>
          );
        })}
      </div>
      <Button kind="ghost" size="sm" renderIcon={Add} onClick={addSegment}>
        Add segment
      </Button>
    </div>
  );
}
